import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FilePlus2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { act, useFetch } from "@/lib/hooks";
import { can, da, fmtDate, km } from "@/lib/format";
import { Empty, Info, Loading, PageHeader, Panel, PdfButton, Photos, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ReceptionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: r, reload } = useFetch<any>(`/receptions/${id}`);
  const [force, setForce] = useState("");
  if (!r) return <Loading />;

  const newQuote = async () => {
    const res = await act(() => api.post("/quotes", { reception_id: r.id, lines: [] }), "Devis créé");
    if (res) nav(`/devis/${res.data.id}`);
  };
  const restitute = async () => {
    if (await act(() => api.post(`/receptions/${r.id}/restitution`, { force_reason: force }), "Véhicule restitué")) reload();
  };
  const activeQuote = r.quotes.find((q: any) => q.status !== "remplace");

  return (
    <div>
      <PageHeader eyebrow={`Réception ${r.number}`} title={r.plate}
        actions={<>
          <PdfButton kind="reception" id={r.id} label="Bon d'entrée" />
          {can(user?.role, "quotes") && !activeQuote && r.status === "presente" && <Button onClick={newQuote} data-testid="create-quote-btn"><FilePlus2 className="mr-2 h-4 w-4" />Créer le devis</Button>}
        </>}>
        <div className="mt-3 flex items-center gap-3"><StatusBadge status={r.status} testId="reception-status" /><span className="text-sm text-muted-foreground">{r.vehicle_label} · {r.client_name} · {r.client_phone}</span></div>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="État à l'arrivée" className="lg:col-span-2">
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            <Info label="Kilométrage" value={km(r.mileage)} testId="reception-mileage" />
            <Info label="Carburant" value={`${r.fuel_level}/8`} />
            <Info label="Clés" value={r.keys_count} />
            <Info label="Entrée" value={fmtDate(r.created_at)} />
          </div>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <Info label="Demande du client" value={r.customer_request} />
            <Info label="Dégâts constatés" value={r.damages || "Aucun"} />
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {Object.entries(r.checklist || {}).map(([k, v]) => (
              <span key={k} className={`rounded-sm border px-2 py-1 text-xs ${v ? "border-emerald-500/40 text-emerald-300 [.light_&]:text-emerald-700" : "border-border text-muted-foreground line-through"}`}>{k}</span>
            ))}
          </div>
        </Panel>
        <Panel title="Suivi">
          <div className="space-y-3 text-sm">
            {r.quotes.map((q: any) => (
              <Link key={q.id} to={`/devis/${q.id}`} className="flex items-center justify-between rounded-sm border border-border p-3 hover:bg-accent" data-testid={`reception-quote-${q.id}`}>
                <span className="font-mono-data">{q.number} v{q.version}</span><StatusBadge status={q.status} />
              </Link>
            ))}
            {r.orders.map((o: any) => (
              <Link key={o.id} to={`/or/${o.id}`} className="flex items-center justify-between rounded-sm border border-border p-3 hover:bg-accent" data-testid={`reception-order-${o.id}`}>
                <span className="font-mono-data">{o.number}</span><StatusBadge status={o.status} />
              </Link>
            ))}
            {can(user?.role, "billing") && r.invoices.map((i: any) => (
              <Link key={i.id} to={`/factures/${i.id}`} className="flex items-center justify-between rounded-sm border border-border p-3 hover:bg-accent">
                <span className="font-mono-data">{i.number || "Brouillon"} · {da(i.totals.ttc)}</span><StatusBadge status={i.payment_status} />
              </Link>
            ))}
            {!r.quotes.length && <Empty text="Pas encore de devis" />}
          </div>
          {r.status === "presente" && (
            <div className="mt-6 border-t border-border pt-4">
              <div className="eyebrow mb-2">Restitution</div>
              {user?.role === "gerant" && <Input value={force} onChange={(e) => setForce(e.target.value)} placeholder="Motif pour forcer (gérant)" className="mb-2" data-testid="restitution-force-input" />}
              <Button variant="outline" className="h-12 w-full" onClick={restitute} data-testid="restitution-btn"><KeyRound className="mr-2 h-4 w-4" />Restituer le véhicule</Button>
            </div>
          )}
          {r.status === "restitue" && <p className="mt-4 text-sm text-muted-foreground">Restitué le {fmtDate(r.returned_at)} par {r.returned_by}{r.force_reason ? ` (forcé : ${r.force_reason})` : ""}</p>}
        </Panel>
        <Panel title={`Photos de réception (${r.photos.length})`} className="lg:col-span-3" testId="reception-photos">
          <Photos entity="receptions" entityId={r.id} photos={r.photos} onAdded={reload} canUpload={r.status === "presente"} />
        </Panel>
      </div>
    </div>
  );
}
